// From wix-design-systems
import { useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';

/** A hook for creating a debounced callback function */
const useDebouncedCallback = (
  /** any function you want to debounce */
  cb: (...args: any[]) => any,
  /** dependencies your callback relies on.
   * Callback will be reevaluated when dependencies change -
   * (same as React's useCallback hook) */
  dependencies: React.DependencyList,
  /** debounce timeout in milliseconds (default: 200) */
  debounceMs: number = 200,
  /** (callback: Function, debounceMs: number) => Function
   * allow passing a custom debounce function (default: lodash debounce) */
  debounceFn: typeof _.debounce = _.debounce,
) => {
  const callbackRef = useRef(cb);
  const debouncedCallback = useCallback(
    debounceFn((...args: any[]) => {
      callbackRef.current(...args);
    }, debounceMs),
    [debounceMs],
  );
  // update callback function when its dependencies change
  useEffect(() => {
    callbackRef.current = cb;
  }, [cb, dependencies]);

  return debouncedCallback;
};

export default useDebouncedCallback;
